<script lang="ts">
	import type { PartnerLinkProps } from '$lib/components/advertising/partners/partnerLink/types';
	import type {
		PartnerColors,
		PartnerCta,
	} from '$lib/components/advertising/partners/types';
	import type { RouteWithGeometry } from '$lib/components/map/types';
	import type { ClassValue } from 'svelte/elements';
	import { page } from '$app/state';
	import {
		getPartnerCardsContent,
	} from '$lib/components/advertising/partners/card/partnerCardsContent';
	import { getPartnerCardData } from '$lib/components/route/routeSelector/utils';
	import { allowedAdServedIds } from '$lib/services/advertising/orange/adserving';
	import { getPageName } from '$lib/services/urls/routeIds';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { convertRgboObjectToRgba } from '$lib/utils/color';
	import { convertDistanceUnitsToMeters } from '$lib/utils/distance';
	import PartnerCard from './PartnerCard.svelte';
	import {
		type Cta,
		type PartnerCardStaticContent,
		type PartnerCardTrackingFunction,
	} from './types';

	interface Props {
		route: RouteWithGeometry | undefined;
		class?: ClassValue;
		trackOnClick?: PartnerCardTrackingFunction;
		trackOnLoad?: PartnerCardTrackingFunction;
	}

	const {
		route,
		class: classes,
		trackOnClick,
		trackOnLoad,
	}: Props = $props();

	const searchParams = getSearchParamsStore();
	const staticData = getPartnerCardsContent();
	const distance = $derived(route?.routeDistance);

	const pageName = $derived(getPageName(page));
	const locale = $derived(page.data.locale);
	const { itineraryResultsStore } = $derived(page.data);

	const partnerCardsData = $derived.by(() => {
		const staticPartnerData = getPartnerCardData(staticData, pageName, convertDistanceUnitsToMeters(distance), locale);
		const staticPartnerDataMapped = staticPartnerData.map(mapPartnerDataToPartnerCardProps).flat().sort((a, b) => a.priority - b.priority);
		return staticPartnerDataMapped;
	});

	function getTrackingData() {
		return $allowedAdServedIds && {
			searchParams: $searchParams,
			route,
			arrivalAddress: $itineraryResultsStore?.arrivalAddress,
			departureAddress: $itineraryResultsStore?.departureAddress,
			waypoints: $itineraryResultsStore?.waypoints,
			advertisers: partnerCardsData
				.filter(({ adservingId }) => !adservingId || $allowedAdServedIds.includes(adservingId))
				.map(({ partnerName }) => partnerName)
				.join('|'),
		};
	}

	function mapPartnerDataToPartnerCardProps(data: PartnerCardStaticContent) {
		return {
			partnerName: data.type,
			priority: data.priority,
			adservingId: data.adservingId,
			title: data.title,
			description: data.description,
			nextDescription: data.nextDescription,
			brandImage: getBrandImage(data),
			cta: parseCTA(data.cta),
			legalNotice: data.legalNotice,
			textStyle: getTextStyle(data),
			backgroundStyle: getBackgroundStyle(data),
		};
	}

	function getBackgroundStyle(partnerCardContent: PartnerCardStaticContent) {
		const backgroundColor = partnerCardContent.styles.backgroundColor;

		const images = partnerCardContent.contentImage;
		const backgroundStyles
			= `background-color: ${getColorValue(backgroundColor)};`;
		const backgroundImageStyle
			= images
				&& `background-image: url(${images.w1920});
			background-image: -webkit-image-set(
				url(${images.w160}) 1x,
				url(${images.w960}) 2x,
			);
			background-image: image-set(
				url(${images.w160}) 1x,
				url(${images.w960}) 2x,
			);
			background-repeat: no-repeat;
			background-position: center;
			background-size: cover;`;

		return `${backgroundStyles || ''}
		${backgroundImageStyle || ''}`;
	}

	function getTextStyle(partnerCardContent: PartnerCardStaticContent) {
		const textStyle = partnerCardContent.styles.textColor;
		return textStyle && `color: ${getColorValue(textStyle)};`;
	}

	function parseCTA(ctaData: PartnerCta | Cta): PartnerLinkProps {
		const {
			label,
			url,
			styles: { backgroundColors, foregroundColors, shadowColor, borderRadius },
			clickUrl,
			impressionUrl,
		} = ctaData;

		return {
			label,
			href: url,
			enabled: getStyles({ label: foregroundColors.enabled, bg: backgroundColors.enabled }, shadowColor),
			hover: getStyles({ label: foregroundColors.hover, bg: backgroundColors.hover }, shadowColor),
			focus: getStyles({ label: foregroundColors.focus, bg: backgroundColors.focus }, shadowColor),
			pressed: getStyles({ label: foregroundColors.pressed, bg: backgroundColors.pressed }, shadowColor),
			radius: borderRadius?.toString() ?? '0',
			clickUrl,
			impressionUrl,
		};
	}

	function getStyles(colors: { label: string | PartnerColors; bg: string | PartnerColors }, shadowColor: string | PartnerColors) {
		return {
			label: getColorValue(colors.label),
			bg: getColorValue(colors.bg),
			boxShadow: getColorValue(shadowColor),
		};
	}

	function getBrandImage(partnerCardContent: PartnerCardStaticContent) {
		const image = partnerCardContent.brandImage;

		const srcset
			= image
				&& Object.entries(image)
					.map(([key, url]) => `${url} ${key}`)
					.join(', ');

		return {
			srcset,
		};
	}

	function getColorValue(color: string | PartnerColors) {
		return typeof color === 'string' ? color : convertRgboObjectToRgba(color);
	}

	const partnerCards = $derived(partnerCardsData.filter(({ partnerName, adservingId }) => partnerName !== 'TURO' || (adservingId && $allowedAdServedIds.includes(adservingId))));
</script>

{#if partnerCards.length > 0}
	<div
		class={['partners flex lt-md:flex-col md:flex-wrap gap-2 items-center sm:items-stretch w-full my-2', classes]}
	>
		{#each partnerCards as partnerCard (partnerCard.partnerName)}
			{#key partnerCard.adservingId && $allowedAdServedIds.includes(partnerCard.adservingId)}
				<PartnerCard
					{...partnerCard}
					onclick={() => {
						const trackingData = getTrackingData();
						trackOnClick?.({ ...trackingData, advertiser: partnerCard.partnerName });
					}}
					onMount={() => {
						const trackingData = getTrackingData();
						trackOnLoad?.({ ...trackingData, advertiser: partnerCard.partnerName });
					}}
				/>
			{/key}
		{/each}
	</div>
{/if}

<style>
	@media screen and (min-width: 960px) {
		.partners :global(> *) {
			flex: 1 1 calc(50% - 8px); /* 8px correspond to the gap */
		}
	}
	.partners {
		scroll-snap-align: start;
	}
</style>
